// V2 Images gallery — every image the signed-in owner has generated, newest
// first, with a soft-hide ("Remove") action. Mirrors library-page.ts: load on
// connect, loading/empty/error states, and the deck-card kebab menu's two-step
// inline confirm (so a hide is never a single misclick). Light DOM so the shared
// home.css chrome (.lib-* / .deck-card / .menu / .btn / .faint) and the
// image-gallery styles in home.css apply directly.

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, HttpError } from '../api/client';
import { setPageTitle } from '../lib/page-title';
import type { GalleryImage } from '../api/types';

/** Human label for the generation style enum (falls back to the raw value). */
const STYLE_LABELS: Record<string, string> = {
  'editorial-photo': 'Editorial photo',
  'abstract-gradient': 'Abstract gradient',
  'line-illustration': 'Line illustration',
  '3d-render': '3D render',
  duotone: 'Duotone',
};

@customElement('images-page')
export class ImagesPage extends LitElement {
  @state() private images: GalleryImage[] = [];
  @state() private loading = true;
  @state() private error = '';
  @state() private menuFor = '';
  @state() private confirmHide = '';

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Images', 'Every image you have generated for your decks.');
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      this.images = await api.listImages();
    } catch (e) {
      this.error = e instanceof HttpError ? `Could not load images (${e.status}).` : 'Could not load images.';
    } finally {
      this.loading = false;
    }
  }

  /** A small style · theme caption for a card. Empty for pre-feature images. */
  private metaLabel(g: GalleryImage): string {
    const parts: string[] = [];
    if (g.style) parts.push(STYLE_LABELS[g.style] ?? g.style);
    if (g.theme) parts.push(g.theme.charAt(0).toUpperCase() + g.theme.slice(1));
    return parts.join(' · ');
  }

  /** Soft-hide an image (owner-only). Optimistic: dropped from the grid
   *  immediately, restored on failure. The menu's two-step confirm gates this
   *  so it is never a single misclick. The bytes keep being served so already-
   *  shared decks never 404 — it just leaves this gallery. */
  private async hide(g: GalleryImage): Promise<void> {
    this.menuFor = '';
    this.confirmHide = '';
    const prev = this.images;
    this.images = this.images.filter((i) => i.id !== g.id);
    try {
      await api.hideImage(g.id);
    } catch {
      this.images = prev;
      this.error = 'Could not remove image.';
    }
  }

  render(): TemplateResult {
    return html`
      <div class="library library--single">
        <main class="lib-main">
          <div class="lib-bar">
            <div class="lib-bar-l">
              <h1 class="lib-h1">Images</h1>
              <span class="lib-count faint">${this.images.length} image${this.images.length === 1 ? '' : 's'}</span>
            </div>
          </div>
          ${this.error ? html`<div class="ws-error">${this.error}</div>` : nothing}
          ${this.loading
            ? html`<div class="lib-empty">Loading…</div>`
            : this.images.length === 0
              ? html`<div class="lib-empty">
                  <div class="lib-empty-ico">▦</div>
                  <p>No images yet. Images you generate in the Studio show up here.</p>
                </div>`
              : html`<div class="img-grid">
                  ${this.images.map((g) => this.renderCard(g))}
                </div>`}
        </main>
      </div>`;
  }

  private renderCard(g: GalleryImage): TemplateResult {
    const title = g.subject || 'Untitled';
    const label = this.metaLabel(g);
    return html`
      <div class="img-card">
        <div class="img-thumb">
          <img src=${g.src} alt=${title} loading="lazy">
        </div>
        <div class="img-meta">
          <div class="img-meta-top">
            <h3 class="img-title" title=${title}>${title}</h3>
            <button
              class="icon-btn img-more"
              aria-label="More actions for ${title}"
              aria-haspopup="true"
              aria-expanded=${this.menuFor === g.id ? 'true' : 'false'}
              @click=${() => { const open = this.menuFor === g.id; this.menuFor = open ? '' : g.id; this.confirmHide = ''; }}
            >⋯</button>
          </div>
          ${label ? html`<div class="img-sub"><span class="faint">${label}</span></div>` : nothing}
          ${this.menuFor === g.id
            ? html`<div class="menu" role="menu" @click=${(e: Event) => e.stopPropagation()}>
                ${this.confirmHide === g.id
                  ? html`
                    <div class="menu-confirm">
                      <p class="menu-confirm-q">Remove “${title}”?</p>
                      <p class="menu-confirm-sub faint">It leaves this gallery; decks that use it keep working.</p>
                      <div class="menu-confirm-row">
                        <button class="btn btn--sm btn--ghost" @click=${() => { this.confirmHide = ''; }}>Cancel</button>
                        <button class="btn btn--sm btn--danger" @click=${() => this.hide(g)}>Remove</button>
                      </div>
                    </div>`
                  : html`
                    <button class="menu-item menu-item--danger" role="menuitem" @click=${() => { this.confirmHide = g.id; }}>🗑 Remove from gallery</button>`}
              </div>`
            : nothing}
        </div>
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'images-page': ImagesPage }
}
